import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ChevronLeft, Loader2, Printer, Share2, Copy, Check, KeyRound } from 'lucide-react';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import brandLogo from '@/assets/newlogo.svg';
import { resolveFileUrl } from '@/utils/fileUrl';
import { parseReportSections } from '@/utils/reportSections';
import MilestoneDocumentView from '@/components/MilestoneDocumentView';
import { EVENTS, track } from '@/lib/analytics';
import type { EmployerReportColor, EmployerReportPayload, EmployerReportVerificationRow } from '@/types';

// pdf.js renders the supporting document PDFs (including permission-encrypted ones,
// which pdf-lib cannot copy) page by page into the exported report.
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1';

type ReportMode = 'owner' | 'pin';

// NestJS sometimes returns a structured `{ message, error, statusCode }` payload
// nested under the top-level `message` field. Walk down until we find a string.
function extractErrorMessage(raw: unknown): string | undefined {
    if (raw == null) return undefined;
    if (typeof raw === 'string') return raw;
    if (Array.isArray(raw)) {
        for (const item of raw) {
            const found = extractErrorMessage(item);
            if (found) return found;
        }
        return undefined;
    }
    if (typeof raw === 'object') {
        const obj = raw as Record<string, unknown>;
        return extractErrorMessage(obj.message) ?? extractErrorMessage(obj.error) ?? undefined;
    }
    return undefined;
}

async function fetchOwnerReport(id: string): Promise<EmployerReportPayload | { status: number; message?: string }> {
    const token = localStorage.getItem('access_token');
    if (!token) return { status: 401 };
    const res = await fetch(`${API_BASE}/investigations/${id}/employer-report`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        let message: string | undefined;
        try {
            message = extractErrorMessage(await res.json());
        } catch {
            /* not JSON */
        }
        return message ? { status: res.status, message } : { status: res.status };
    }
    const body = await res.json();
    return body?.data ?? body;
}

async function fetchReportWithPin(id: string, pin: string): Promise<EmployerReportPayload> {
    const res = await fetch(`${API_BASE}/investigations/${id}/employer-report/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
    });
    if (!res.ok) {
        let message: string | undefined;
        try {
            message = extractErrorMessage(await res.json());
        } catch {
            /* not JSON */
        }
        if (res.status === 403) throw new Error(message || 'Invalid PIN. Try again.');
        if (res.status === 400) throw new Error(message || 'This report is not yet available.');
        if (res.status === 404) throw new Error(message || 'Report not found.');
        throw new Error(message || 'Failed to load report.');
    }
    const body = await res.json();
    return body?.data ?? body;
}

const COLOR_HEX: Record<EmployerReportColor, string> = {
    green: '#00d440',
    orange: '#ffaa00',
    red: '#ff4e64',
};

function colorFromToken(token?: EmployerReportColor | string): string {
    if (token && token in COLOR_HEX) return COLOR_HEX[token as EmployerReportColor];
    return COLOR_HEX.green;
}

// Fetch a (possibly cross-origin) image and inline it as a base64 data URL.
// html2canvas can't rasterize a cross-origin <img> without tainting the canvas —
// the image then silently drops out of the exported PDF. Inlining the bytes as a
// same-origin data URL sidesteps that entirely. Returns '' if the fetch is blocked
// (e.g. the host serves the file without CORS), in which case we fall back to the
// raw URL for on-screen display.
async function imageToDataUrl(url: string): Promise<string> {
    try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) return '';
        const blob = await res.blob();
        return await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
            reader.onerror = () => resolve('');
            reader.readAsDataURL(blob);
        });
    } catch {
        return '';
    }
}

// Copy every readable CSS rule into a <style> tag inside the html2canvas clone
// and drop its <link rel="stylesheet"> tags. html2canvas re-fetches linked
// stylesheets over the network inside its clone iframe; if that re-fetch fails
// (proxy/rewrite rules, CSP, slow network) the clone renders with NO styles and
// the exported PDF comes out as unstyled stacked text. Inlining the already-
// loaded CSSOM makes the clone self-contained.
function inlineStylesIntoClone(cloneDoc: Document): void {
    const css: string[] = [];
    for (const sheet of Array.from(document.styleSheets)) {
        try {
            const rules = sheet.cssRules;
            if (!rules) continue;
            css.push(
                Array.from(rules)
                    .map((rule) => rule.cssText)
                    .join('\n'),
            );
        } catch {
            // Cross-origin or extension-injected stylesheet — unreadable, skip it.
        }
    }
    if (css.length === 0) return;
    cloneDoc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => link.remove());
    const style = cloneDoc.createElement('style');
    style.textContent = css.join('\n');
    cloneDoc.head.appendChild(style);
}

// ── Attachable documents (rendered inline + appended to the exported PDF) ──────

interface ReportAttachment {
    id: string;
    label: string;
    fileName?: string;
    // Direct static URL — used for the "Open" link (plain navigation, no CORS).
    fileUrl: string;
    // API route that streams the same bytes with CORS + no-store, so the browser
    // can read them for the inline preview and the "Save as PDF" embed even when
    // the CDN-cached /uploads path has no usable CORS headers.
    fetchUrl: string;
    mimeType?: string;
}

// A document type can be re-uploaded; the backend keeps every file, so collapse to
// the most recent per type.
function dedupeSupporting<T extends { documentType: string; uploadedAt: string }>(list: T[]): T[] {
    return Object.values(
        list.reduce((acc: Record<string, T>, doc) => {
            const prev = acc[doc.documentType];
            if (!prev || new Date(doc.uploadedAt).getTime() >= new Date(prev.uploadedAt).getTime()) {
                acc[doc.documentType] = doc;
            }
            return acc;
        }, {}),
    );
}

const isEmbeddable = (mime?: string): boolean => !!mime && (mime === 'application/pdf' || mime.startsWith('image/'));

// The supporting documents (Police Character Certificate, Notarized Affidavit,
// Social Media Report, etc.) that can be rendered — PDFs and images. Word docs
// can't be embedded, so they stay as download links only.
function getReportAttachments(
    investigationId: string,
    documents?: EmployerReportPayload['documents'],
): ReportAttachment[] {
    if (!documents) return [];
    return dedupeSupporting(documents.supportingDocuments ?? [])
        .filter((doc) => isEmbeddable(doc.mimeType))
        .map((doc) => ({
            id: doc.id,
            label: humanizeDocType(doc.documentType),
            fileName: doc.fileName,
            fileUrl: doc.fileUrl,
            fetchUrl: `${API_BASE}/investigations/${investigationId}/documents/${doc.id}/file`,
            mimeType: doc.mimeType,
        }));
}

interface PdfLayout {
    margin: number;
    pageW: number;
    pageH: number;
    contentW: number;
}

// Add a full-width image page to the report, optionally with a title above it.
function addImagePage(
    pdf: jsPDF,
    dataUrl: string,
    imgW: number,
    imgH: number,
    title: string | null,
    layout: PdfLayout,
) {
    const { margin, pageH, contentW } = layout;
    pdf.addPage();
    let top = margin;
    if (title) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(20, 20, 20);
        pdf.text(title, margin, margin + 3);
        top = margin + 8;
    }
    const availH = pageH - top - margin;
    let w = contentW;
    let h = (imgH / imgW) * contentW;
    if (h > availH) {
        const s = availH / h;
        h = availH;
        w = w * s;
    }
    pdf.addImage(dataUrl, 'JPEG', margin + (contentW - w) / 2, top, w, h, undefined, 'FAST');
}

// Append each attachment to the generated report PDF. PDFs are rasterized page by
// page with pdf.js (which renders permission-encrypted files that pdf-lib can't
// copy); images are placed on their own page. Failures skip that attachment rather
// than breaking the export; the skipped labels are returned so the UI can say
// which documents are missing from the file.
async function appendAttachmentsToReport(
    pdf: jsPDF,
    attachments: ReportAttachment[],
    layout: PdfLayout,
): Promise<string[]> {
    const failed: string[] = [];
    for (const att of attachments) {
        let bytes: ArrayBuffer;
        try {
            // Fetch through the API stream route (CORS + no-store), not the raw
            // /uploads path whose CDN-cached copies drop the CORS headers.
            const res = await fetch(att.fetchUrl, { method: 'GET' });
            if (!res.ok) {
                failed.push(att.label);
                continue;
            }
            bytes = await res.arrayBuffer();
        } catch {
            failed.push(att.label);
            continue;
        }

        try {
            if (att.mimeType === 'application/pdf') {
                const doc = await pdfjsLib.getDocument({ data: new Uint8Array(bytes) }).promise;
                for (let i = 1; i <= doc.numPages; i++) {
                    const page = await doc.getPage(i);
                    // Scale 1.5 keeps document text legible at A4 while cutting the
                    // rasterized page size well below a scale-2 render.
                    const viewport = page.getViewport({ scale: 1.5 });
                    const canvas = document.createElement('canvas');
                    canvas.width = Math.floor(viewport.width);
                    canvas.height = Math.floor(viewport.height);
                    const ctx = canvas.getContext('2d');
                    if (!ctx) continue;
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    addImagePage(pdf, dataUrl, canvas.width, canvas.height, i === 1 ? att.label : null, layout);
                }
                await doc.cleanup();
            } else if (att.mimeType?.startsWith('image/')) {
                const dataUrl = await imageToDataUrl(att.fetchUrl);
                if (!dataUrl) continue;
                const img = new Image();
                img.src = dataUrl;
                await img.decode().catch(() => undefined);
                addImagePage(pdf, dataUrl, img.naturalWidth || 800, img.naturalHeight || 1000, att.label, layout);
            }
        } catch {
            // Unreadable/unsupported file — skip it.
            failed.push(att.label);
        }
    }
    return failed;
}

export default function EmployerReport() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    type Stage = 'loading' | 'pin-required' | 'rendered' | 'fatal';
    const [stage, setStage] = useState<Stage>('loading');
    const [mode, setMode] = useState<ReportMode>('owner');
    const [data, setData] = useState<EmployerReportPayload | null>(null);
    const [fatalMessage, setFatalMessage] = useState('');

    useEffect(() => {
        let cancelled = false;
        if (!id) {
            setStage('fatal');
            setFatalMessage('Missing investigation id.');
            return () => {
                cancelled = true;
            };
        }

        (async () => {
            const result = await fetchOwnerReport(id);
            if (cancelled) return;
            if ('status' in result) {
                if (result.status === 401 || result.status === 403) {
                    // No JWT, or JWT doesn't grant access — fall through to PIN gate.
                    setStage('pin-required');
                    return;
                }
                if (result.status === 400) {
                    setStage('fatal');
                    setFatalMessage(result.message || 'This report is not yet available.');
                    return;
                }
                if (result.status === 404) {
                    setStage('fatal');
                    setFatalMessage(result.message || 'Report not found.');
                    return;
                }
                setStage('fatal');
                setFatalMessage(result.message || 'Failed to load report.');
                return;
            }
            setData(result);
            setMode('owner');
            setStage('rendered');
        })();

        return () => {
            cancelled = true;
        };
    }, [id]);

    if (stage === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#EDEFF3]">
                <Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" />
            </div>
        );
    }

    if (stage === 'pin-required' && id) {
        return (
            <PinGate
                investigationId={id}
                onUnlocked={(payload) => {
                    setData(payload);
                    setMode('pin');
                    setStage('rendered');
                }}
            />
        );
    }

    if (stage === 'fatal' || !data) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#EDEFF3] px-6">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="mb-4 flex items-center gap-2 text-sm text-[#1b1b1b] hover:underline"
                >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                </button>
                <div className="bg-white rounded-2xl shadow-sm px-6 py-8 max-w-md text-center">
                    <p className="text-sm text-[#4e4d4d]">
                        {(typeof fatalMessage === 'string' && fatalMessage) || 'This report is not available.'}
                    </p>
                </div>
            </div>
        );
    }

    return <EmployerReportContent data={data} mode={mode} />;
}

function PinGate({
    investigationId,
    onUnlocked,
}: {
    investigationId: string;
    onUnlocked: (data: EmployerReportPayload) => void;
}) {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        const cleaned = pin.replace(/\D/g, '');
        if (cleaned.length < 4 || cleaned.length > 10) {
            setError('PIN must be 4–10 digits.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const result = await fetchReportWithPin(investigationId, cleaned);
            onUnlocked(result);
        } catch (err: any) {
            setError(err?.message || 'Failed to unlock report.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#EDEFF3] px-6">
            <form onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl shadow-sm p-6 md:p-8">
                <div className="w-12 h-12 rounded-full bg-[#FF6B35]/10 flex items-center justify-center mb-4">
                    <KeyRound className="w-5 h-5 text-[#FF6B35]" />
                </div>
                <h1 className="text-xl font-bold text-[#151515] mb-1">Enter PIN to view report</h1>
                <p className="text-sm text-[#4e4d4d] mb-6">
                    Someone shared this background-check report with you. Enter the PIN they sent to unlock it.
                </p>

                <label className="block text-sm font-medium text-[#1b1b1b] mb-1">Report PIN</label>
                <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={pin}
                    onChange={(e) => {
                        setPin(e.target.value);
                        setError('');
                    }}
                    maxLength={10}
                    placeholder="e.g. 847291"
                    className="w-full bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 text-base font-mono tracking-widest outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15"
                />
                {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

                <button
                    type="submit"
                    disabled={submitting}
                    className="mt-4 w-full bg-[#151515] hover:bg-[#2a2a2a] disabled:opacity-60 text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {submitting ? 'Unlocking…' : 'View report'}
                </button>
            </form>
        </div>
    );
}

function EmployerReportContent({ data, mode }: { data: EmployerReportPayload; mode: ReportMode }) {
    const { branding, fieldReport, riskGauge, verificationBreakdown, header } = data;
    const supportCode = header?.supportCode;
    const reportPin = mode === 'owner' ? header?.reportPin : null;
    const ringStroke = colorFromToken(riskGauge.color);
    // Prefer the backend-inlined base64 passport (survives the html2canvas -> PDF
    // export with no CORS dependency). Fall back to the file URL for display, which
    // the export step still tries to inline itself.
    const passportPhotoSrc =
        header?.employee?.passportPhotoDataUrl ||
        resolveFileUrl(header?.employee?.passportPhotoUrl || data.documents?.passportPhoto?.fileUrl);

    // SVG ring geometry.
    const RING_SIZE = 140;
    const RING_STROKE = 8;
    const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;

    const reportCardRef = useRef<HTMLDivElement>(null);
    const extrasRef = useRef<HTMLDivElement>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState('');
    // Passport inlined as a data URL so it survives the html2canvas → PDF export.
    const [passportDataUrl, setPassportDataUrl] = useState('');

    useEffect(() => {
        let cancelled = false;
        if (!passportPhotoSrc || /^data:/i.test(passportPhotoSrc)) {
            setPassportDataUrl('');
            return;
        }
        imageToDataUrl(passportPhotoSrc).then((dataUrl) => {
            if (!cancelled) setPassportDataUrl(dataUrl);
        });
        return () => {
            cancelled = true;
        };
    }, [passportPhotoSrc]);

    const handleExportPdf = async () => {
        const node = reportCardRef.current;
        if (!node || isExporting) return;
        setIsExporting(true);
        setExportError('');

        // Ensure the passport is inlined before we rasterize. If the preload hasn't
        // finished (or hasn't run), do it now and let the <img> swap to the data URL
        // on the next frame so html2canvas captures it.
        if (passportPhotoSrc && !passportDataUrl && !/^data:/i.test(passportPhotoSrc)) {
            const dataUrl = await imageToDataUrl(passportPhotoSrc);
            if (dataUrl) {
                setPassportDataUrl(dataUrl);
                await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
            }
        }

        const extras = extrasRef.current;
        const disclaimer = node.querySelector<HTMLElement>('[data-pdf-disclaimer]');
        const savedDisclaimerDisplay = disclaimer?.style.display ?? '';

        // Hide the inline document previews for the whole export. html2canvas
        // clones the ENTIRE document for every capture, and the previews' <object>
        // PDF embeds would load megabytes of plugin content inside each clone.
        // The actual documents are appended at full quality at the end anyway.
        // Also hide any section flagged data-pdf-exclude (the on-screen Documents
        // list with View/Open links — dead in a printout, and the documents
        // themselves are appended at the end).
        const hiddenForExport: { el: HTMLElement; display: string }[] = [];
        const hideForExport = (el: HTMLElement | null) => {
            if (!el) return;
            hiddenForExport.push({ el, display: el.style.display });
            el.style.display = 'none';
        };
        hideForExport(extras?.querySelector<HTMLElement>('[data-attachment-previews]') ?? null);
        extras?.querySelectorAll<HTMLElement>('[data-pdf-exclude]').forEach((el) => hideForExport(el));

        // Inline EVERY remote <img> in the card and the supplementary sections
        // (visit photos, etc.) as data URLs. html2canvas cannot rasterize
        // cross-origin images — they taint the canvas and silently drop out of the
        // export. Swap each src to inlined bytes for the capture and restore the
        // originals afterwards.
        const swappedImgs: { img: HTMLImageElement; src: string }[] = [];
        const remoteImgs = [
            ...Array.from(node.querySelectorAll('img')),
            ...(extras ? Array.from(extras.querySelectorAll('img')) : []),
        ].filter((img) => {
            const src = img.getAttribute('src') ?? '';
            return src !== '' && !/^data:/i.test(src);
        });
        await Promise.all(
            remoteImgs.map(async (img) => {
                const src = img.getAttribute('src')!;
                const dataUrl = await imageToDataUrl(src);
                if (dataUrl) {
                    swappedImgs.push({ img, src });
                    img.src = dataUrl;
                }
            }),
        );
        // Let the swapped images decode before rasterizing.
        await Promise.all(swappedImgs.map(({ img }) => img.decode().catch(() => undefined)));

        try {
            // 1. Capture the disclaimer on its own so we can stamp it on every page.
            let disclaimerCanvas: HTMLCanvasElement | null = null;
            if (disclaimer) {
                disclaimerCanvas = await html2canvas(disclaimer, {
                    scale: 2,
                    backgroundColor: '#ffffff',
                    useCORS: true,
                    logging: false,
                    onclone: inlineStylesIntoClone,
                });
                // 2. Hide it during the main capture so we can stamp it ourselves later.
                disclaimer.style.display = 'none';
            }

            // 3. Capture the main card (without the disclaimer). Scale 1.5 keeps
            // text crisp at A4 while keeping the rasterized page much lighter than
            // a scale-2 capture.
            const mainCanvas = await html2canvas(node, {
                scale: 1.5,
                backgroundColor: '#ffffff',
                useCORS: true,
                logging: false,
                windowWidth: node.scrollWidth,
                onclone: inlineStylesIntoClone,
            });

            // Restore disclaimer visibility before we do any further work.
            if (disclaimer) disclaimer.style.display = savedDisclaimerDisplay;

            // 3b. Capture the supplementary sections (address visits, milestones,
            // documents list) so the exported PDF shows everything on this page.
            // The inline attachment previews stay hidden (see above) — the actual
            // documents are appended at full quality further down, and capturing
            // the previews too would duplicate them.
            let extrasCanvas: HTMLCanvasElement | null = null;
            if (extras) {
                extrasCanvas = await html2canvas(extras, {
                    scale: 1.5,
                    backgroundColor: '#ffffff',
                    useCORS: true,
                    logging: false,
                    windowWidth: extras.scrollWidth,
                    onclone: inlineStylesIntoClone,
                });
            }

            // 4. Build PDF: portrait A4, 8mm margin per side.
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const margin = 8;
            const contentW = pageW - margin * 2;

            // Disclaimer height in mm (placed at the bottom of every page).
            const disclaimerHmm = disclaimerCanvas ? (disclaimerCanvas.height / disclaimerCanvas.width) * contentW : 0;
            const disclaimerData = disclaimerCanvas?.toDataURL('image/png');

            // Usable area for main content per page (mm).
            const gap = 4; // breathing room above the disclaimer
            const usableHmm = pageH - margin * 2 - disclaimerHmm - gap;

            // 5+6. Slice a rendered canvas into per-page chunks and paint each into
            // the PDF. We scan the canvas pixel-by-pixel and prefer breaks at rows
            // that are mostly white, so we don't split a line of text or a card in
            // half. The disclaimer is stamped at the bottom of every page.
            let pageStarted = false;
            const addCanvasAsPages = (canvas: HTMLCanvasElement) => {
                const canvasPxPerMm = canvas.width / contentW;
                const canvasUsableHpx = Math.floor(usableHmm * canvasPxPerMm);
                const ctx = canvas.getContext('2d')!;
                const allPixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
                const rowIsWhitespace = (y: number) => {
                    const start = y * canvas.width * 4;
                    for (let i = 0; i < canvas.width; i += 4) {
                        const o = start + i * 4;
                        const r = allPixels[o] ?? 255;
                        const g = allPixels[o + 1] ?? 255;
                        const b = allPixels[o + 2] ?? 255;
                        if (r < 245 || g < 245 || b < 245) return false;
                    }
                    return true;
                };

                const findCleanBreak = (idealY: number): number => {
                    // Search back up to 15% of a page for the nearest whitespace row.
                    const maxBack = Math.floor(canvasUsableHpx * 0.15);
                    for (let y = idealY; y >= idealY - maxBack && y > 0; y--) {
                        if (rowIsWhitespace(y)) return y;
                    }
                    return idealY;
                };

                let sliceTop = 0;
                const tmpCanvas = document.createElement('canvas');
                const tmpCtx = tmpCanvas.getContext('2d')!;

                while (sliceTop < canvas.height) {
                    const remainingPx = canvas.height - sliceTop;
                    const idealEnd = sliceTop + Math.min(canvasUsableHpx, remainingPx);
                    const sliceEnd = idealEnd >= canvas.height ? canvas.height : findCleanBreak(idealEnd);
                    const slicePx = sliceEnd - sliceTop;
                    if (slicePx <= 0) break;

                    tmpCanvas.width = canvas.width;
                    tmpCanvas.height = slicePx;
                    tmpCtx.fillStyle = '#ffffff';
                    tmpCtx.fillRect(0, 0, tmpCanvas.width, tmpCanvas.height);
                    tmpCtx.drawImage(canvas, 0, sliceTop, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
                    // JPEG (not PNG) — the report is white background with text and a
                    // photo, so JPEG at 0.82 is a fraction of the PNG size with no
                    // visible loss at print scale.
                    const sliceData = tmpCanvas.toDataURL('image/jpeg', 0.82);
                    const sliceHmm = slicePx / canvasPxPerMm;

                    if (pageStarted) pdf.addPage();
                    pageStarted = true;
                    pdf.addImage(sliceData, 'JPEG', margin, margin, contentW, sliceHmm, undefined, 'FAST');

                    // Disclaimer stamped at the bottom of every page.
                    if (disclaimerData) {
                        pdf.addImage(
                            disclaimerData,
                            'PNG',
                            margin,
                            pageH - margin - disclaimerHmm,
                            contentW,
                            disclaimerHmm,
                        );
                    }

                    sliceTop = sliceEnd;
                }
            };

            addCanvasAsPages(mainCanvas);
            // Supplementary sections (address visits, milestones, documents list)
            // continue on a fresh page after the main report.
            if (extrasCanvas) addCanvasAsPages(extrasCanvas);

            const subject =
                data.header?.employee?.fullName?.replace(/\s+/g, '-').toLowerCase() ||
                data.header?.investigationId?.slice(0, 8) ||
                'report';
            const fileName = `podus-checker-report-${subject}.pdf`;

            // Append the actual supporting documents (police certificate, notarized
            // affidavit, social media report, …) as pages after the report.
            const attachments = getReportAttachments(header.investigationId, data.documents);
            if (attachments.length > 0) {
                await appendAttachmentsToReport(pdf, attachments, {
                    margin,
                    pageW,
                    pageH,
                    contentW,
                });
            }
            pdf.save(fileName);

            // Client-side only — there's no API call for the middleware to observe.
            // Deliberately excludes `subject`/`fileName`: both carry the employee's name.
            track(EVENTS.EMPLOYER_REPORT_DOWNLOADED, {
                investigationId: header.investigationId,
                attachmentCount: attachments.length,
                pageCount: pdf.getNumberOfPages(),
            });
        } catch (err: any) {
            setExportError(err?.message || 'Failed to export PDF.');
            track(EVENTS.EMPLOYER_REPORT_DOWNLOAD_FAILED, { investigationId: header.investigationId });
        } finally {
            if (disclaimer) disclaimer.style.display = savedDisclaimerDisplay;
            hiddenForExport.forEach(({ el, display }) => {
                el.style.display = display;
            });
            swappedImgs.forEach(({ img, src }) => {
                img.src = src;
            });
            setIsExporting(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-[#f1f3f6] print:bg-white"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
            {/* Print rules — preserve colours and the two-column layout when saving to PDF */}
            <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          html, body { background: #ffffff !important; }
          *, *::before, *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          /* Avoid orphan section headings */
          section, aside { break-inside: avoid; }
        }
      `}</style>
            {/* Top toolbar — hidden when printing */}
            <div className="sticky top-0 z-10 bg-[#f1f3f6]/80 backdrop-blur-sm border-b border-[#e0e0e0] print:hidden">
                <div className="mx-auto max-w-[1100px] px-4 md:px-6 py-3 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => window.close()}
                        className="flex items-center gap-2 text-sm font-medium text-[#1b1b1b] hover:opacity-80"
                        title="Close this tab"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Close
                    </button>
                    <div className="flex items-center gap-3">
                        {exportError && <span className="text-xs text-red-600">{exportError}</span>}
                        {reportPin && <ShareButton pin={reportPin} investigationId={header.investigationId} />}
                        <button
                            type="button"
                            onClick={handleExportPdf}
                            disabled={isExporting}
                            className="flex items-center gap-2 bg-[#151515] hover:bg-[#2a2a2a] disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium"
                        >
                            {isExporting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Printer className="w-4 h-4" />
                            )}
                            {isExporting ? 'Generating…' : 'Save as PDF'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-[1100px] px-4 md:px-6 py-8 print:px-0 print:py-0 flex flex-col gap-6 print:gap-3">
                {/* Main report card */}
                <div
                    ref={reportCardRef}
                    className="bg-white rounded-2xl shadow-sm print:shadow-none print:rounded-none overflow-hidden"
                >
                    {/* Header strip */}
                    <div className="border-b border-[#f0f0f0] px-8 py-5 flex items-start gap-4 flex-wrap">
                        <div className="flex items-center gap-3 shrink-0">
                            <img src={brandLogo} alt={branding.productName} className="h-7 object-contain" />
                        </div>
                        <p className="flex-1 min-w-[200px] text-[11px] leading-relaxed text-[#747474]">
                            {branding.intro}
                        </p>
                        {supportCode && (
                            <div className="shrink-0 text-right">
                                <p className="text-[10px] uppercase tracking-wider text-[#717680] mb-0.5">
                                    Support code
                                </p>
                                <p className="font-mono text-xs font-semibold text-[#1b1b1b] tracking-[0.04em]">
                                    {supportCode}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Subject identity strip — passport photo + name */}
                    <div className="border-b border-[#f0f0f0] px-8 py-5 flex items-center gap-4">
                        {passportPhotoSrc ? (
                            <img
                                src={passportDataUrl || passportPhotoSrc}
                                alt={header?.employee?.fullName || 'Subject'}
                                className="w-[120px] h-[150px] rounded-md object-cover border border-[#e0e0e0] bg-[#f5f5f5] shrink-0"
                            />
                        ) : (
                            <div
                                className="w-[120px] h-[150px] rounded-md border border-dashed border-[#e0e0e0] bg-[#f5f5f5] shrink-0 flex items-center justify-center"
                                aria-hidden="true"
                            >
                                <span className="text-[10px] text-[#9aa0a6] text-center px-1 leading-tight">
                                    No photo
                                </span>
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="text-[10px] uppercase tracking-wider text-[#717680] mb-1">
                                Subject of report
                            </p>
                            <p className="text-lg font-semibold text-[#151515] leading-tight truncate">
                                {header?.employee?.fullName || '—'}
                            </p>
                            {header?.employee?.gender && (
                                <p className="text-xs text-[#4e4d4d] mt-1 capitalize">{header.employee.gender}</p>
                            )}
                        </div>
                    </div>

                    {/* Body — matches the Figma's gray-tinted section cards */}
                    <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-[1fr_260px] print:grid-cols-[1fr_220px] gap-6 print:gap-4">
                        {/* Left column: narrative */}
                        <div className="flex flex-col gap-4 min-w-0">
                            <section className="bg-[#f4f4f4]/75 rounded-md p-5">
                                <h2 className="text-base font-semibold text-[#040404] mb-2">Executive Summary</h2>
                                <p className="text-sm text-[#4b4b4b] leading-relaxed whitespace-pre-wrap text-justify">
                                    {fieldReport.executiveSummary || (
                                        <span className="italic text-[#9aa0a6]">No executive summary provided.</span>
                                    )}
                                </p>
                            </section>

                            <section className="bg-[#f4f4f4]/75 rounded-md p-5">
                                <h2 className="text-base font-semibold text-[#040404] mb-2">Detailed Report</h2>
                                <DetailedReportBody raw={fieldReport.details} />
                            </section>
                        </div>

                        {/* Right column: gauge + breakdown — single gray card per Figma */}
                        <aside className="bg-[#f4f4f4]/75 rounded-md p-4 flex flex-col gap-3 min-w-0">
                            {/* Risk donut */}
                            <div className="flex flex-col items-center pt-1 pb-2">
                                <div className="relative" style={{ width: RING_SIZE, height: RING_SIZE }}>
                                    <svg
                                        width={RING_SIZE}
                                        height={RING_SIZE}
                                        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                                        fill="none"
                                    >
                                        <circle
                                            cx={RING_SIZE / 2}
                                            cy={RING_SIZE / 2}
                                            r={RING_RADIUS}
                                            stroke="#e8e8e8"
                                            strokeWidth={RING_STROKE}
                                        />
                                        <circle
                                            cx={RING_SIZE / 2}
                                            cy={RING_SIZE / 2}
                                            r={RING_RADIUS}
                                            stroke={ringStroke}
                                            strokeWidth={RING_STROKE}
                                            strokeLinecap="round"
                                            strokeDasharray={2 * Math.PI * RING_RADIUS}
                                            strokeDashoffset={
                                                2 *
                                                Math.PI *
                                                RING_RADIUS *
                                                (1 - Math.max(0, Math.min(100, riskGauge.riskPercentage)) / 100)
                                            }
                                            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                                        />
                                    </svg>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <p className="text-2xl font-semibold text-[#040404] leading-none">
                                            {riskGauge.riskPercentage}%
                                        </p>
                                    </div>
                                </div>
                                <p className="text-sm font-semibold mt-3" style={{ color: ringStroke }}>
                                    {riskGauge.riskLevelLabel}
                                </p>
                            </div>

                            {/* Verification breakdown — rows, no header per Figma */}
                            {verificationBreakdown.length === 0 ? (
                                <p className="text-xs text-[#4b4b4b] px-1 py-2">No verification data available.</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {verificationBreakdown.map((row: EmployerReportVerificationRow, i) => {
                                        // Not applicable for this tier, or no substantive findings
                                        // recorded — show a muted "N/A" instead of a percentage.
                                        const isNA = row.applicable === false || row.verificationPercentage == null;
                                        return (
                                            <div
                                                key={`${row.category}-${i}`}
                                                className="bg-white rounded-md px-3 py-2.5 flex items-center justify-between gap-2"
                                            >
                                                <p className="text-xs font-semibold text-[#4b4b4b] truncate">
                                                    {row.label}
                                                </p>
                                                {isNA ? (
                                                    <p className="text-xs font-semibold shrink-0 text-[#9aa0a6]">N/A</p>
                                                ) : (
                                                    <p
                                                        className="text-xs font-semibold shrink-0"
                                                        style={{ color: colorFromToken(row.color ?? undefined) }}
                                                    >
                                                        {row.verificationPercentage}%
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </aside>
                    </div>

                    {/* Disclaimer */}
                    <div data-pdf-disclaimer className="border-t border-[#f0f0f0] px-8 py-4">
                        <p className="text-[10px] leading-relaxed text-[#747474]">{branding.disclaimer}</p>
                    </div>
                </div>

                {/* Supplementary sections — captured into the exported PDF after the
                    main card; excluded from the browser's native print flow */}
                <div ref={extrasRef} className="flex flex-col gap-6 print:hidden">
                    <AttachmentPreviewSection investigationId={header.investigationId} documents={data.documents} />
                    <AddressVisitsSection visits={data.addressVisits} />
                    {data.milestoneDocument && data.milestoneDocument.blocks.length > 0 ? (
                        <MilestoneDocumentView document={data.milestoneDocument} />
                    ) : (
                        <ChecklistSummarySection checklist={data.checklist} />
                    )}
                    <DocumentsSection documents={data.documents} />
                </div>
            </div>
        </div>
    );
}

const FINDING_LABEL: Record<string, string> = {
    yes: 'Yes',
    no: 'No',
    n_a: 'N/A',
};

const FINDING_PILL: Record<string, string> = {
    yes: 'bg-green-50 text-green-700 border-green-200',
    no: 'bg-red-50 text-red-700 border-red-200',
    n_a: 'bg-gray-50 text-gray-700 border-gray-200',
};

// Render the agent's detailed-report body. When it follows the numbered
// "1. Heading / body" structure produced by the section editor, each section
// gets its own typographic block; otherwise we fall through to the legacy
// whitespace-pre-wrap rendering so older free-form reports still display.
function DetailedReportBody({ raw }: { raw: string | null | undefined }) {
    if (!raw || !raw.trim()) {
        return (
            <p className="text-sm text-[#4b4b4b] leading-relaxed whitespace-pre-wrap text-justify">
                <span className="italic text-[#9aa0a6]">No detailed report provided.</span>
            </p>
        );
    }

    const sections = parseReportSections(raw);
    const isStructured = sections.some((s) => s.heading.trim().length > 0);

    if (!isStructured) {
        return <p className="text-sm text-[#4b4b4b] leading-relaxed whitespace-pre-wrap text-justify">{raw}</p>;
    }

    // Number only the sections that have a heading — preamble-style entries
    // (heading === '') keep their original ordering but stay unnumbered.
    let n = 0;
    return (
        <div className="flex flex-col gap-4 text-justify">
            {sections.map((section, idx) => {
                if (section.heading.trim()) {
                    n += 1;
                    return (
                        <div key={idx}>
                            <p className="text-sm font-semibold text-[#040404] mb-1">
                                {n}. {section.heading}
                            </p>
                            <p className="text-sm text-[#4b4b4b] leading-relaxed whitespace-pre-wrap">{section.body}</p>
                        </div>
                    );
                }
                return (
                    <p key={idx} className="text-sm text-[#4b4b4b] leading-relaxed whitespace-pre-wrap">
                        {section.body}
                    </p>
                );
            })}
        </div>
    );
}

function ChecklistSummarySection({ checklist }: { checklist?: EmployerReportPayload['checklist'] }) {
    if (!checklist || checklist.sections.length === 0) return null;
    return (
        <div className="bg-white rounded-2xl shadow-sm print:shadow-none print:rounded-none">
            <div className="px-6 py-5">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <h3 className="text-base font-bold text-[#1b1b1b]">Investigator checklist</h3>
                    <span className="text-xs text-[#4e4d4d]">
                        <span className="font-semibold text-[#1b1b1b]">{checklist.answeredItems}</span>
                        {' / '}
                        {checklist.totalItems} answered
                        {checklist.isComplete && (
                            <span className="ml-2 inline-block text-[10px] font-medium px-2 py-0.5 rounded-md bg-green-50 text-green-700 border border-green-200">
                                Complete
                            </span>
                        )}
                    </span>
                </div>

                <div className="flex flex-col gap-4">
                    {checklist.sections.map((section) => (
                        <div key={section.id} className="border border-[#f0f0f0] rounded-lg">
                            <p className="text-sm font-semibold text-[#1b1b1b] px-3 py-2 bg-[#fafafa] border-b border-[#f0f0f0] rounded-t-lg">
                                {section.title}
                            </p>
                            <ul className="divide-y divide-[#f5f5f5]">
                                {section.items.map((item) => {
                                    const finding = item.finding ?? 'n_a';
                                    const pillClass = item.finding
                                        ? (FINDING_PILL[finding] ?? FINDING_PILL.n_a)
                                        : 'bg-gray-100 text-gray-500 border-gray-200';
                                    const pillText = item.finding ? (FINDING_LABEL[finding] ?? finding) : 'Unanswered';
                                    return (
                                        <li key={item.itemId} className="px-3 py-2.5">
                                            <div className="flex items-start justify-between gap-3">
                                                <p className="text-xs text-[#1b1b1b] leading-snug flex-1 min-w-0">
                                                    {item.question}
                                                </p>
                                                <span
                                                    className={`shrink-0 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-md border ${pillClass}`}
                                                >
                                                    {pillText}
                                                </span>
                                            </div>
                                            {item.notes && (
                                                <p className="text-[11px] text-[#4e4d4d] whitespace-pre-wrap mt-1 leading-relaxed">
                                                    {item.notes}
                                                </p>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const VISIT_TYPE_LABEL: Record<string, string> = {
    home: 'Home',
    work: 'Workplace',
    reference: 'Reference',
    guarantor: 'Guarantor',
    guarantor_1: 'Guarantor 1',
    guarantor_2: 'Guarantor 2',
    prior_employer: 'Prior Employer',
    education: 'Education',
    other: 'Other',
};

function AddressVisitsSection({ visits }: { visits?: EmployerReportPayload['addressVisits'] }) {
    if (!visits || visits.length === 0) return null;
    return (
        <div className="bg-white rounded-2xl shadow-sm print:shadow-none print:rounded-none">
            <div className="px-6 py-5">
                <h3 className="text-base font-bold text-[#1b1b1b] mb-3">Address visits</h3>
                <div className="flex flex-col gap-3">
                    {visits.map((v) => {
                        const visitedWhen = v.visitedAt
                            ? new Date(v.visitedAt).toLocaleString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                              })
                            : '';
                        const photos = Array.isArray(v.photos) ? v.photos : [];
                        const rawLat = v.location?.latitude;
                        const rawLng = v.location?.longitude;
                        const lat = typeof rawLat === 'number' || typeof rawLat === 'string' ? Number(rawLat) : null;
                        const lng = typeof rawLng === 'number' || typeof rawLng === 'string' ? Number(rawLng) : null;
                        const hasCoords = lat !== null && lng !== null && Number.isFinite(lat) && Number.isFinite(lng);
                        const accuracy = v.location?.accuracyMeters;
                        return (
                            <div key={v.id} className="border border-[#f0f0f0] rounded-lg p-3">
                                <div className="flex items-start justify-between gap-3 mb-1">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <span className="inline-block text-[11px] font-medium px-2 py-0.5 rounded-md bg-[#FF6B35]/10 text-[#FF6B35] capitalize">
                                                {VISIT_TYPE_LABEL[v.visitType] ??
                                                    String(v.visitType).replace(/_/g, ' ')}
                                            </span>
                                            {visitedWhen && (
                                                <span className="text-[11px] text-[#717680]">{visitedWhen}</span>
                                            )}
                                            {typeof accuracy === 'number' && (
                                                <span className="text-[11px] text-[#717680]">±{accuracy} m</span>
                                            )}
                                        </div>
                                        <p className="text-sm font-semibold text-[#1b1b1b] leading-snug">
                                            {v.observedAddress}
                                        </p>
                                        {hasCoords && (
                                            <p className="text-[11px] font-mono text-[#717680] mt-0.5">
                                                {lat!.toFixed(5)}, {lng!.toFixed(5)}
                                            </p>
                                        )}
                                    </div>
                                    {v.location?.mapsUrl && (
                                        <a
                                            href={v.location.mapsUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="shrink-0 bg-white border border-[#e0e0e0] hover:bg-gray-50 text-[#1b1b1b] px-3 py-1.5 rounded-lg text-xs font-medium"
                                        >
                                            View on map
                                        </a>
                                    )}
                                </div>
                                {v.notes && (
                                    <p className="text-xs text-[#4e4d4d] whitespace-pre-wrap leading-relaxed mt-2">
                                        {v.notes}
                                    </p>
                                )}
                                {photos.length > 0 && (
                                    <div className="mt-2 flex gap-2 flex-wrap">
                                        {photos.map((url, i) => {
                                            const resolved = resolveFileUrl(url);
                                            return (
                                                <a
                                                    key={`${url}-${i}`}
                                                    href={resolved}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="block w-14 h-14 rounded-md overflow-hidden border border-[#f0f0f0] bg-[#f5f5f5]"
                                                    title={`Photo ${i + 1}`}
                                                >
                                                    <img src={resolved} alt="" className="w-full h-full object-cover" />
                                                </a>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function formatFileSize(bytes?: number): string {
    if (!bytes || bytes <= 0) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

function DocumentRow({
    label,
    doc,
}: {
    label: string;
    doc: EmployerReportPayload['documents'] extends infer D
        ? D extends { passportPhoto?: infer F | null }
            ? F | null | undefined
            : never
        : never;
}) {
    if (!doc) {
        return (
            <div className="flex items-center justify-between gap-3 py-2.5 border-b border-[#f0f0f0] last:border-b-0">
                <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1b1b1b]">{label}</p>
                    <p className="text-xs text-[#9aa0a6]">Not provided</p>
                </div>
            </div>
        );
    }
    return (
        <div className="flex items-center justify-between gap-3 py-2.5 border-b border-[#f0f0f0] last:border-b-0">
            <div className="min-w-0">
                <p className="text-sm font-medium text-[#1b1b1b] truncate">{label}</p>
                <p className="text-xs text-[#4e4d4d] truncate">
                    {doc.fileName}
                    {doc.fileSize ? ` · ${formatFileSize(doc.fileSize)}` : ''}
                    {doc.mimeType ? ` · ${doc.mimeType}` : ''}
                </p>
            </div>
            <a
                href={resolveFileUrl(doc.fileUrl)}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 bg-white border border-[#e0e0e0] text-[#1b1b1b] hover:bg-gray-50 px-3 py-1.5 rounded-lg text-xs font-medium"
            >
                View
            </a>
        </div>
    );
}

function DocumentsSection({ documents }: { documents?: EmployerReportPayload['documents'] }) {
    if (!documents) return null;
    // Show only the most recent file per document type.
    const supporting = dedupeSupporting(documents.supportingDocuments ?? []);
    const hasAnything =
        documents.passportPhoto || documents.governmentId || documents.signedAffidavit || supporting.length > 0;
    if (!hasAnything) return null;

    return (
        // Excluded from the exported PDF — the View/Open links are dead in a
        // printout and the actual documents are appended in full further down.
        // On-screen it stays, where the links work.
        <div data-pdf-exclude className="bg-white rounded-2xl shadow-sm print:shadow-none print:rounded-none">
            <div className="px-6 py-5">
                <h3 className="text-base font-bold text-[#1b1b1b] mb-3">Documents</h3>
                <div className="flex flex-col">
                    <DocumentRow label="Passport Photo" doc={documents.passportPhoto} />
                    <DocumentRow label="Government ID" doc={documents.governmentId} />
                    {supporting.length > 0 && (
                        <div className="mt-4">
                            <p className="text-xs font-semibold text-[#4e4d4d] uppercase tracking-wide mb-2">
                                Supporting Documents
                            </p>
                            {supporting.map((doc) => (
                                <div
                                    key={doc.id}
                                    className="flex items-center justify-between gap-3 py-2.5 border-b border-[#f0f0f0] last:border-b-0"
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-[#1b1b1b] truncate">
                                            {humanizeDocType(doc.documentType)}
                                        </p>
                                        <p className="text-xs text-[#4e4d4d] truncate">
                                            {doc.fileName}
                                            {doc.fileSize ? ` · ${formatFileSize(doc.fileSize)}` : ''}
                                            {doc.mimeType ? ` · ${doc.mimeType}` : ''}
                                        </p>
                                    </div>
                                    <a
                                        href={resolveFileUrl(doc.fileUrl)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="shrink-0 bg-white border border-[#e0e0e0] text-[#1b1b1b] hover:bg-gray-50 px-3 py-1.5 rounded-lg text-xs font-medium"
                                    >
                                        View
                                    </a>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Inline, on-page preview of the supporting documents (Police Character
// Certificate, Notarized Affidavit, Social Media Report, …). PDFs are rendered
// page by page with pdf.js; images display directly. Hidden during the PDF
// export capture — the export appends the same documents at full quality via
// appendAttachmentsToReport, so rendering them here too would duplicate them.
function AttachmentPreviewSection({
    investigationId,
    documents,
}: {
    investigationId: string;
    documents?: EmployerReportPayload['documents'];
}) {
    const attachments = getReportAttachments(investigationId, documents);
    if (attachments.length === 0) return null;
    return (
        <div data-attachment-previews className="flex flex-col gap-6">
            {attachments.map((att) => (
                <AttachmentPreviewCard key={att.id} attachment={att} />
            ))}
        </div>
    );
}

function AttachmentPreviewCard({ attachment }: { attachment: ReportAttachment }) {
    const [pages, setPages] = useState<string[]>([]);
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        setPages([]);

        (async () => {
            try {
                if (attachment.mimeType === 'application/pdf') {
                    // Plain GET, no extra headers — a "simple" request that needs no
                    // CORS preflight (see handleDownloadDocument in
                    // InvestigationDetails). Reading the bytes still requires the file
                    // host to send Access-Control-Allow-Origin; if it doesn't, we fall
                    // back to the browser's native PDF viewer via <object> below.
                    const res = await fetch(attachment.fetchUrl, { method: 'GET' });
                    if (!res.ok) throw new Error('fetch failed');
                    const bytes = await res.arrayBuffer();
                    const doc = await pdfjsLib.getDocument({ data: new Uint8Array(bytes) }).promise;
                    const rendered: string[] = [];
                    for (let i = 1; i <= doc.numPages && !cancelled; i++) {
                        const page = await doc.getPage(i);
                        const viewport = page.getViewport({ scale: 1.5 });
                        const canvas = document.createElement('canvas');
                        canvas.width = Math.floor(viewport.width);
                        canvas.height = Math.floor(viewport.height);
                        const ctx = canvas.getContext('2d');
                        if (!ctx) continue;
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
                        rendered.push(canvas.toDataURL('image/jpeg', 0.85));
                    }
                    await doc.cleanup();
                    if (!cancelled) {
                        setPages(rendered);
                        setStatus(rendered.length > 0 ? 'ready' : 'error');
                    }
                } else {
                    const dataUrl = await imageToDataUrl(attachment.fetchUrl);
                    if (!cancelled) {
                        // Fall back to the direct static URL for on-screen <img> if the
                        // byte-inline failed (display tolerates it; the PDF embed can't).
                        setPages([dataUrl || resolveFileUrl(attachment.fileUrl)]);
                        setStatus('ready');
                    }
                }
            } catch {
                if (!cancelled) setStatus('error');
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [attachment.id, attachment.fileUrl, attachment.mimeType]);

    return (
        <div className="bg-white rounded-2xl shadow-sm print:shadow-none print:rounded-none">
            <div className="px-6 py-5">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-[#1b1b1b]">{attachment.label}</h3>
                        {attachment.fileName && (
                            <p className="text-xs text-[#4e4d4d] truncate mt-0.5">{attachment.fileName}</p>
                        )}
                    </div>
                    <a
                        href={resolveFileUrl(attachment.fileUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 bg-white border border-[#e0e0e0] text-[#1b1b1b] hover:bg-gray-50 px-3 py-1.5 rounded-lg text-xs font-medium"
                    >
                        Open
                    </a>
                </div>

                {status === 'loading' && (
                    <div className="flex items-center justify-center gap-2 py-10 text-sm text-[#717680]">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading preview…
                    </div>
                )}
                {status === 'error' &&
                    (attachment.mimeType === 'application/pdf' ? (
                        // Byte-level fetch was blocked (file host sends no CORS headers)
                        // — embed via the browser's own PDF viewer instead, which needs
                        // no CORS. Note: this fallback view cannot be captured into the
                        // exported PDF; that needs CORS enabled on the file host.
                        <object
                            data={resolveFileUrl(attachment.fileUrl)}
                            type="application/pdf"
                            className="w-full h-[75vh] rounded-md border border-[#e0e0e0] bg-[#f5f5f5]"
                            aria-label={attachment.label}
                        >
                            <p className="py-8 text-center text-sm text-[#9aa0a6]">
                                Preview unavailable — use "Open" to view this document.
                            </p>
                        </object>
                    ) : (
                        <p className="py-8 text-center text-sm text-[#9aa0a6]">
                            Preview unavailable — use "Open" to view this document.
                        </p>
                    ))}
                {status === 'ready' && (
                    <div className="flex flex-col gap-3">
                        {pages.map((src, i) => (
                            <img
                                key={`${attachment.id}-${i}`}
                                src={src}
                                alt={`${attachment.label} — page ${i + 1}`}
                                className="w-full rounded-md border border-[#e0e0e0] bg-white"
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ShareButton({ pin, investigationId }: { pin: string; investigationId: string }) {
    const [open, setOpen] = useState(false);
    const [copiedPin, setCopiedPin] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Close popover when clicking outside.
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        window.addEventListener('mousedown', onDocClick);
        return () => window.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const shareUrl = `${window.location.origin}/investigations/${investigationId}/report`;

    const copy = async (value: string, kind: 'pin' | 'link') => {
        try {
            await navigator.clipboard.writeText(value);
            if (kind === 'pin') {
                setCopiedPin(true);
                window.setTimeout(() => setCopiedPin(false), 1500);
            } else {
                setCopiedLink(true);
                window.setTimeout(() => setCopiedLink(false), 1500);
            }
        } catch {
            /* clipboard rejected */
        }
    };

    return (
        <div className="relative" ref={popoverRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 bg-white border border-[#e0e0e0] hover:bg-gray-50 text-[#1b1b1b] px-4 py-2 rounded-lg text-sm font-medium"
            >
                <Share2 className="w-4 h-4" />
                Share
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-[320px] bg-white border border-[#e0e0e0] rounded-xl shadow-lg p-4 z-20">
                    <p className="text-sm font-semibold text-[#151515] mb-1">Share this report</p>
                    <p className="text-xs text-[#4e4d4d] mb-3">
                        Send the link below and the PIN to whoever needs to view this report. They'll be prompted for
                        the PIN before the report unlocks.
                    </p>

                    <label className="block text-[11px] uppercase tracking-wider text-[#717680] mb-1">Report PIN</label>
                    <div className="flex items-center gap-2 bg-[#fafafa] border border-[#e0e0e0] rounded-lg px-3 py-2 mb-3">
                        <span className="flex-1 font-mono text-base font-bold text-[#151515] tracking-[0.2em]">
                            {pin}
                        </span>
                        <button
                            type="button"
                            onClick={() => copy(pin, 'pin')}
                            className="text-xs font-medium text-[#FF6B35] hover:underline flex items-center gap-1"
                        >
                            {copiedPin ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            {copiedPin ? 'Copied' : 'Copy'}
                        </button>
                    </div>

                    <label className="block text-[11px] uppercase tracking-wider text-[#717680] mb-1">Link</label>
                    <div className="flex items-center gap-2 bg-[#fafafa] border border-[#e0e0e0] rounded-lg px-3 py-2">
                        <span className="flex-1 text-xs text-[#1b1b1b] truncate">{shareUrl}</span>
                        <button
                            type="button"
                            onClick={() => copy(shareUrl, 'link')}
                            className="shrink-0 text-xs font-medium text-[#FF6B35] hover:underline flex items-center gap-1"
                        >
                            {copiedLink ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            {copiedLink ? 'Copied' : 'Copy'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function humanizeDocType(type: string): string {
    if (!type) return 'Document';
    const map: Record<string, string> = {
        cv: 'CV / Resume',
        national_id: 'National ID',
        nin: 'NIN',
        passport: 'Passport',
        agent_report: 'Agent Report',
        investigation_report: 'Investigation Report',
        other: 'Other',
    };
    return map[type] ?? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
